import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { QuoteService } from '../../shared/services/quote.service';
import { Quote, QuoteListResponse } from '../../models/quote-response.interface';
import { QuoteDetailsComponent } from '../../components/quote-details/quote-details.component';
import { LoaderComponent } from '../../components/loader/loader.component';
import { TranslateModule } from '@ngx-translate/core';
import { CompanyService } from '../../shared/services/company.service';

@Component({
  selector: 'app-active-quotes',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, LoaderComponent, TranslateModule],
  templateUrl: './active-quotes.component.html',
  styleUrls: ['./active-quotes.component.scss'],
})
export class ActiveQuotesComponent implements OnInit {
  quotes: Quote[] = [];
  allQuotes: Quote[] = [];
  selectedQuote: Quote | null = null;
  isLoading = false;
  error: string | null = null;
  totalElements = 0;
  currentPage = 0;
  pageSize = 20;
  Math = Math; // Make Math available in template
  currentSelectedCompanyId: number = 0;

  // Search and filter properties
  searchTerm: string = '';
  statusFilter: string = '';
  dateSort: string = 'desc';
  private searchTimeout: any;

  constructor(private quoteService: QuoteService, private router: Router, private companyService : CompanyService) {}

  ngOnInit(): void {
    this.companyService.currentSelectedCompany$.subscribe(companyId => {
      this.currentSelectedCompanyId = companyId;
      this.loadActiveQuotes();
    });
  }

  ngOnDestroy(): void {
    // Clear search timeout to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  loadActiveQuotes(page: number = 0): void {
    this.isLoading = true;
    this.error = null;

    this.quoteService.getActiveQuotes(this.currentSelectedCompanyId, this.searchTerm, page, this.pageSize).subscribe({
      next: (response: QuoteListResponse) => {
        console.log('Active Quotes Response:', response);
        this.allQuotes = response.content;
        this.quotes = response.content;
        this.totalElements = response.totalElements;
        this.currentPage = response.number;
        this.pageSize = response.size;
        this.isLoading = false;
      },
      error: (err: any) => {
        this.error = 'Failed to load quotes. Please try again later.';
        this.isLoading = false;
        console.error('Error loading quotes:', err);
      },
    });
  }

  selectQuote(quote: Quote): void {
    this.selectedQuote = quote;
    // Navigate to the employee details page with the selected quote with company id
    this.router.navigate(['/employee-details/active'], {
      queryParams: { companyId: quote.companyId || quote.quoteRequest?.companyId }
    });
  }

  closeDetails(): void {
    this.selectedQuote = null;
  }

  // Search and filter methods
  onSearchChange(): void {
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.loadActiveQuotes();
    }, 500);
  }

  hasSearchCriteria(): boolean{
    return this.searchTerm.trim().length > 0;
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.loadActiveQuotes();
  }

  getDateFromArray(dateArray: number[] | undefined): Date {
    if (!dateArray || dateArray.length < 3) return new Date();
    const [year, month, day] = dateArray;
    return new Date(year, month - 1, day);
  }

  // Helper method to get date from either new or old API format
  getQuoteDate(quote: Quote): Date {
    if (quote.requestDate) {
      return new Date(quote.requestDate);
    }
    if (quote.quoteRequest?.requestDate) {
      return this.getDateFromArray(quote.quoteRequest.requestDate);
    }
    return new Date();
  }

  // Action methods
  viewQuote(quote: Quote): void {
    this.selectQuote(quote);
  }

  convertToPolicy(quote: Quote): void {
    // Navigate to payment or policy conversion page
    this.router.navigate(['/payment'], {
      queryParams: {
        quotaReferenceNo: quote.quoteRequestReferenceNo || quote.quoteRequest?.quoteRequestReferenceNo,
        quotaresponceId: quote.id
      }
    });
  }

  createNewQuote(): void {
    this.router.navigate(['/company-details']);
  }

  getExpiryDate(quote: Quote): string {
    const expiryDate = this.getExpiryDateObject(quote);
    return expiryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  getExpiryDateObject(quote: Quote): Date {
    // Assuming quotes expire 30 days from request date
    const requestDate = this.getQuoteDate(quote);
    return new Date(requestDate.getTime() + 30 * 24 * 60 * 60 * 1000);
  }

  // Helper method to format date array to readable string
  formatDate(dateArray: number[] | undefined): string {
    if (!dateArray || dateArray.length < 3) return 'N/A';
    const [year, month, day] = dateArray;
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // Helper method to format amount
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  }

  // Helper method to format ISO date string
  formatRequestDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (error) {
      return 'N/A';
    }
  }

  // Helper method to get verification status
  getVerificationStatus(quote: Quote): string {
    return quote.quoteVerification?.status || 'PENDING';
  }

  // Helper method to check if quote has verification
  hasVerification(quote: Quote): boolean {
    return !!quote.quoteVerification;
  }

  // Pagination methods
  goToFirstPage(): void {
    if (this.currentPage > 0) {
      this.loadActiveQuotes(0);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.loadActiveQuotes(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    const totalPages = Math.ceil(this.totalElements / this.pageSize);
    if (this.currentPage < totalPages - 1) {
      this.loadActiveQuotes(this.currentPage + 1);
    }
  }

  goToLastPage(): void {
    const totalPages = Math.ceil(this.totalElements / this.pageSize);
    if (this.currentPage < totalPages - 1) {
      this.loadActiveQuotes(totalPages - 1);
    }
  }

  goToPage(page: number): void {
    const totalPages = Math.ceil(this.totalElements / this.pageSize);
    if (page >= 0 && page < totalPages && page !== this.currentPage) {
      this.loadActiveQuotes(page);
    }
  }

  // Helper methods for pagination
  get totalPages(): number {
    return Math.ceil(this.totalElements / this.pageSize);
  }

  get isFirstPage(): boolean {
    return this.currentPage === 0;
  }

  get isLastPage(): boolean {
    return this.currentPage >= this.totalPages - 1;
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const delta = 2; // Show 2 pages before and after current

    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      range.push(i);
    }

    if (current - delta > 2) {
      rangeWithDots.push(1, -1); // -1 represents dots
    } else {
      for (let i = 1; i < Math.max(2, current - delta); i++) {
        rangeWithDots.push(i);
      }
    }

    rangeWithDots.push(...range);

    if (current + delta < total - 1) {
      rangeWithDots.push(-1, total); // -1 represents dots
    } else {
      for (let i = Math.min(total - 1, current + delta) + 1; i <= total; i++) {
        rangeWithDots.push(i);
      }
    }

    return rangeWithDots.filter(n => n > 0 || n === -1);
  }

  getProviderColor(providerName: string): string {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    let hash = 0;
    for (let i = 0; i < providerName.length; i++) {
      hash = providerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash % colors.length);
    return colors[colorIndex];
  }

  getProviderInitials(providerName: string): string {
    const words = providerName.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return providerName.substring(0, 2).toUpperCase();
  }
}
